import { createClient } from '@supabase/supabase-js';
import { Database } from '../types/database';

// Get Supabase credentials from environment
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Only enter demo mode if credentials are clearly missing or placeholder
const isDemoMode = !supabaseUrl || !supabaseAnonKey ||
                   supabaseUrl === 'https://demo-supabase-url.supabase.co' ||
                   supabaseAnonKey === 'demo-anon-key-placeholder';

// Create Supabase client
const createSupabaseClient = () => {
  if (isDemoMode) {
    console.log('Supabase credentials not found - using offline mode');
    return null;
  }

  console.log('Initializing Supabase with URL:', supabaseUrl);
  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce'
    },
    global: {
      headers: {
        'X-Client-Info': 'lifeshare-app'
      }
    },
    db: {
      schema: 'public'
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  });
};

const supabaseClient = createSupabaseClient();

// Export properly typed client (null when in demo mode)
export const supabase = supabaseClient;

// Auth helper functions
export const signUp = async (email: string, password: string, userData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured. Please check your environment variables.' } };
  }

  try {
    console.log('Starting sign up for:', email);

    const cleanEmail = email.toLowerCase().trim();
    const cleanFullName = userData?.full_name?.trim() || '';
    const userRole = userData?.role || 'donor';

    const { data, error } = await supabaseClient.auth.signUp({
      email: cleanEmail,
      password,
      options: {
        data: {
          full_name: cleanFullName,
          role: userRole
        }
      }
    });

    if (error) {
      console.error('Sign up error:', error);
      if (error.message.includes('already registered')) {
        return { data: null, error: { message: 'An account with this email already exists. Please sign in instead.' } };
      }
      return { data, error };
    }

    console.log('Sign up successful:', data.user?.id);
    return { data, error: null };
  } catch (error: any) {
    console.error('Sign up exception:', error);
    return { data: null, error: { message: error.message || 'Sign up failed' } };
  }
};

export const signIn = async (email: string, password: string) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured. Please check your environment variables.' } };
  }

  try {
    console.log('Starting sign in for:', email);

    const { data, error } = await supabaseClient.auth.signInWithPassword({
      email: email.toLowerCase().trim(),
      password,
    });

    if (error) {
      console.error('Sign in error:', error);
      if (error.message.includes('Invalid login credentials')) {
        return { data: null, error: { message: 'Invalid email or password.' } };
      }
      return { data, error };
    }

    console.log('Sign in successful:', data.user?.id);
    return { data, error: null };
  } catch (error: any) {
    console.error('Sign in exception:', error);
    return { data: null, error: { message: error.message || 'Sign in failed' } };
  }
};

export const signOut = async () => {
  if (!supabaseClient) {
    return { error: null };
  }
  try {
    const { error } = await supabaseClient.auth.signOut();
    if (error) throw error;
    return { error: null };
  } catch (error: any) {
    return { error: { message: error.message || 'Sign out error' } };
  }
};

export const getCurrentUser = async () => {
  if (!supabaseClient) {
    return { user: null, error: null };
  }
  try {
    const { data: { user }, error } = await supabaseClient.auth.getUser();
    return { user, error };
  } catch (error: any) {
    return { user: null, error: { message: error.message || 'Get user error' } };
  }
};

// Profile helpers
export const getProfile = async (userId: string) => {
  if (!supabaseClient) {
    return { data: null, error: { code: 'PGRST116' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Profile fetch error' } };
  }
};

export const createProfile = async (profileData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('profiles')
      .insert(profileData)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Profile creation error' } };
  }
};

export const updateProfile = async (userId: string, updates: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('profiles')
      .update(updates)
      .eq('user_id', userId)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Profile update error' } };
  }
};

// Donor helpers
export const createDonor = async (donorData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('donors')
      .insert(donorData)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Donor creation error' } };
  }
};

export const getDonor = async (profileId: string) => {
  if (!supabaseClient) {
    return { data: null, error: { code: 'PGRST116' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('donors')
      .select('*')
      .eq('profile_id', profileId)
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Donor fetch error' } };
  }
};

export const updateDonor = async (id: string, updates: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('donors')
      .update(updates)
      .eq('id', id)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Donor update error' } };
  }
};

// Recipient helpers
export const createRecipient = async (recipientData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('recipients')
      .insert(recipientData)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Recipient creation error' } };
  }
};

export const getRecipient = async (profileId: string) => {
  if (!supabaseClient) {
    return { data: null, error: { code: 'PGRST116' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('recipients')
      .select('*')
      .eq('profile_id', profileId)
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Recipient fetch error' } };
  }
};

export const updateRecipient = async (id: string, updates: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('recipients')
      .update(updates)
      .eq('id', id)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Recipient update error' } };
  }
};

// Hospital helpers
export const createHospital = async (hospitalData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('hospitals')
      .insert(hospitalData)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Hospital creation error' } };
  }
};

export const getHospital = async (profileId: string) => {
  if (!supabaseClient) {
    return { data: null, error: { code: 'PGRST116' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('hospitals')
      .select('*')
      .eq('profile_id', profileId)
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Hospital fetch error' } };
  }
};

// Organ matches helpers
export const getOrganMatches = async () => {
  if (!supabaseClient) {
    return { data: [], error: null };
  }
  try {
    const { data, error } = await supabaseClient
      .from('organ_matches')
      .select(`
        *,
        donors:donor_id(*),
        recipients:recipient_id(*),
        hospitals:hospital_id(*)
      `)
      .order('created_at', { ascending: false });
    return { data, error };
  } catch (error: any) {
    return { data: [], error: { message: error.message || 'Organ matches fetch error' } };
  }
};

export const createOrganMatch = async (matchData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('organ_matches')
      .insert(matchData)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Organ match creation error' } };
  }
};

export const updateOrganMatch = async (id: string, updates: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('organ_matches')
      .update(updates)
      .eq('id', id)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Organ match update error' } };
  }
};

// Medical records helpers
export const getMedicalRecords = async (profileId: string) => {
  if (!supabaseClient) {
    return { data: [], error: null };
  }
  try {
    const { data, error } = await supabaseClient
      .from('medical_records')
      .select('*')
      .eq('profile_id', profileId)
      .order('test_date', { ascending: false });
    return { data, error };
  } catch (error: any) {
    return { data: [], error: { message: error.message || 'Medical records fetch error' } };
  }
};

export const createMedicalRecord = async (recordData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('medical_records')
      .insert(recordData)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Medical record creation error' } };
  }
};

// Appointments helpers
export const getAppointments = async (profileId: string) => {
  if (!supabaseClient) {
    return { data: [], error: null };
  }
  try {
    const { data, error } = await supabaseClient
      .from('appointments')
      .select('*')
      .eq('profile_id', profileId)
      .order('appointment_date', { ascending: true });
    return { data, error };
  } catch (error: any) {
    return { data: [], error: { message: error.message || 'Appointments fetch error' } };
  }
};

export const createAppointment = async (appointmentData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('appointments')
      .insert(appointmentData)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Appointment creation error' } };
  }
};

// Notifications helpers
export const getNotifications = async (profileId: string) => {
  if (!supabaseClient) {
    return { data: [], error: null };
  }
  try {
    const { data, error } = await supabaseClient
      .from('notifications')
      .select('*')
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false });
    return { data, error };
  } catch (error: any) {
    return { data: [], error: { message: error.message || 'Notifications fetch error' } };
  }
};

export const createNotification = async (notificationData: any) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('notifications')
      .insert(notificationData)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Notification creation error' } };
  }
};

export const markNotificationAsRead = async (id: string) => {
  if (!supabaseClient) {
    return { data: null, error: { message: 'Supabase not configured' } };
  }
  try {
    const { data, error } = await supabaseClient
      .from('notifications')
      .update({ read: true })
      .eq('id', id)
      .select()
      .maybeSingle();
    return { data, error };
  } catch (error: any) {
    return { data: null, error: { message: error.message || 'Notification update error' } };
  }
};
